import { useMemo } from 'react';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  PauseCircle,
  PlayCircle,
} from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { useQueueHealth } from '@/hooks/useEmailBackend';

type Tone = 'green' | 'yellow' | 'red';

/**
 * Super-admin queue-processor health card (Task #24).
 *
 * System-wide view of the email-queue processor:
 *   - Last processor run (timestamp + relative time + status)
 *   - cron `process-email-queue` job active flag
 *   - System-wide pending / processing / overdue counts
 *   - Oldest overdue age (in minutes)
 *   - Last recover-stuck-jobs run (from cron.job_run_details)
 *
 * Status thresholds (overall card badge):
 *   GREEN  — last run <10m ago + cron_job_active=true + no system overdue >10m
 *   YELLOW — last run 10–20m ago, OR overdue >20m, OR cron_job_active=false
 *   RED    — last run >20m ago, OR overdue >60m, OR last_run_status='failed'
 *
 * Mounts inside InfrastructurePanel (already gated by useSuperAdmin in AdminBar).
 * Reuses the same useQueueHealth hook as the workspace card; super-admin
 * privileges are evaluated server-side inside the RPC.
 */
export function QueueProcessorHealthCard() {
  const { currentWorkspace } = useWorkspaces();
  const { data: health, isLoading, error } = useQueueHealth(currentWorkspace?.id);

  const status = useMemo<Tone | null>(() => {
    if (!health) return null;
    const minutesSinceLastRun = health.last_run_started_at
      ? (Date.now() - new Date(health.last_run_started_at).getTime()) / 60000
      : Infinity;
    const overdueMinutes = health.system_oldest_overdue_minutes ?? 0;
    const lastStatus = health.last_run_status;
    const cronActive = health.cron_job_active;

    if (minutesSinceLastRun > 20 || overdueMinutes > 60 || lastStatus === 'failed') {
      return 'red';
    }
    if (minutesSinceLastRun > 10 || overdueMinutes > 20 || cronActive === false) {
      return 'yellow';
    }
    return 'green';
  }, [health]);

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Queue processor health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-red-600">
            Failed to load: {(error as Error).message}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading || !health) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Queue processor health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        </CardContent>
      </Card>
    );
  }

  // RPC returned a row but system-wide fields are NULL → caller is not a
  // super-admin (shouldn't happen here since AdminBar gates on useSuperAdmin,
  // but defend against it).
  const systemFieldsPopulated = health.last_run_started_at !== null
    || health.cron_job_active !== null
    || health.system_total_pending !== null;

  if (!systemFieldsPopulated) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Queue processor health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            Super-admin role required to view processor liveness.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="h-4 w-4" />
          Queue processor health
        </CardTitle>
        {status && <StatusBadge tone={status} />}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Last run + cron active */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              Last processor run
            </div>
            <div className="text-sm font-medium">
              {health.last_run_started_at ? (
                <>
                  {formatDistanceToNowStrict(new Date(health.last_run_started_at), { addSuffix: true })}
                  <span className="block text-xs text-muted-foreground font-normal">
                    {new Date(health.last_run_started_at).toLocaleString()}
                    {health.last_run_status && ` • ${health.last_run_status}`}
                    {typeof health.last_run_duration_ms === 'number'
                      && ` • ${health.last_run_duration_ms}ms`}
                  </span>
                </>
              ) : (
                <span className="text-muted-foreground">No runs recorded</span>
              )}
            </div>
          </div>

          <div>
            <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
              {health.cron_job_active ? (
                <PlayCircle className="h-3.5 w-3.5 text-green-600" />
              ) : (
                <PauseCircle className="h-3.5 w-3.5 text-red-600" />
              )}
              process-email-queue cron
            </div>
            <div className="text-sm font-medium">
              {health.cron_job_active === null
                ? <span className="text-muted-foreground">unknown</span>
                : health.cron_job_active
                  ? <span className="text-green-600">Active</span>
                  : <span className="text-red-600">Inactive</span>}
            </div>
          </div>
        </div>

        {/* Last-run job stats (if we have a run) */}
        {health.last_run_started_at && (
          <div className="text-xs text-muted-foreground border-t pt-3">
            Last run fetched <span className="font-medium text-foreground">{health.last_run_jobs_fetched ?? 0}</span>
            {' '}jobs • <span className="font-medium text-green-700">{health.last_run_jobs_succeeded ?? 0}</span> sent
            {' '}• <span className="font-medium text-red-700">{health.last_run_jobs_failed ?? 0}</span> failed
          </div>
        )}

        {/* System-wide queue depths */}
        <div className="grid grid-cols-3 gap-4 border-t pt-3">
          <Stat
            label="Pending"
            value={health.system_total_pending ?? 0}
            icon={<Clock className="h-3.5 w-3.5 text-yellow-500" />}
          />
          <Stat
            label="Processing"
            value={health.system_processing_count ?? 0}
            icon={<Loader2 className="h-3.5 w-3.5 text-blue-500" />}
          />
          <Stat
            label="Overdue"
            value={health.system_overdue_count ?? 0}
            icon={
              <AlertTriangle
                className={`h-3.5 w-3.5 ${
                  (health.system_overdue_count ?? 0) > 0
                    ? 'text-red-500'
                    : 'text-muted-foreground'
                }`}
              />
            }
            sublabel={
              (health.system_overdue_count ?? 0) > 0
              && typeof health.system_oldest_overdue_minutes === 'number'
                ? `oldest ${health.system_oldest_overdue_minutes}m`
                : undefined
            }
          />
        </div>

        {/* Recover-stuck-jobs cron */}
        <div className="text-xs text-muted-foreground border-t pt-3">
          <span className="font-medium text-foreground">recover-stuck-jobs:</span>{' '}
          {health.recover_cron_last_run_at ? (
            <>
              {formatDistanceToNowStrict(new Date(health.recover_cron_last_run_at), { addSuffix: true })}
              {health.recover_cron_last_status && ` (${health.recover_cron_last_status})`}
            </>
          ) : (
            'no runs recorded'
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ tone }: { tone: Tone }) {
  if (tone === 'green') {
    return (
      <Badge variant="secondary" className="bg-green-100 text-green-700 hover:bg-green-100">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        Healthy
      </Badge>
    );
  }
  if (tone === 'yellow') {
    return (
      <Badge variant="default" className="bg-yellow-500 hover:bg-yellow-500">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Degraded
      </Badge>
    );
  }
  return (
    <Badge variant="destructive">
      <AlertCircle className="h-3 w-3 mr-1" />
      Critical
    </Badge>
  );
}

function Stat({
  label,
  value,
  icon,
  sublabel,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  sublabel?: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-xl font-bold">{value.toLocaleString()}</div>
      {sublabel && (
        <div className="text-xs text-muted-foreground mt-0.5">{sublabel}</div>
      )}
    </div>
  );
}
